'use client'

import Image from 'next/image'
import React, { useState } from 'react'

import { PlayButton } from '@/blocks/VideoEmbed/PlayButton'
import { cn } from '@/utilities/ui'
import type { EventState } from './eventState'
import { CornerBracket } from './icons'
import { StreamEmbed } from './StreamEmbed'
import { MONO_MICRO } from './tokens'

type Props = {
  state: EventState
  /** The stream is on. Later than `soon` begins, so it cannot be derived from the state. */
  streamOn: boolean
  posterSrc: string
  posterAlt: string
  /** Vetted Vimeo URL. Absent means the stream was never configured. */
  liveEmbedUrl: string | null
  /** Vetted YouTube or Vimeo embed URL for the recording. */
  replayEmbedUrl: string | null
  /** The recording's own poster, best first. Empty falls straight through to `posterSrc`. */
  thumbnailSources?: string[]
  thumbnailAlt?: string
  /** Already formatted, e.g. "21 Sept 2026 - 17:00 BST". */
  eventStamp: string
  className?: string
}

/**
 * VR/Video Frame, all four of its states.
 *
 * The bloom and the embed follow `streamOn`, not the state: `soon` starts hours out, while the
 * stream is switched on shortly before the start, and lighting the frame early would promise a
 * broadcast that is not running.
 *
 * The recording is the only state with a play control, and it is the site's shared PlayButton
 * rather than the board's disc, so a replay here behaves like every other video on civo.com.
 */
export function VideoFrame({
  state,
  streamOn,
  posterSrc,
  posterAlt,
  liveEmbedUrl,
  replayEmbedUrl,
  thumbnailSources = [],
  thumbnailAlt,
  eventStamp,
  className,
}: Props) {
  const [playing, setPlaying] = useState(false)
  const [sourceIndex, setSourceIndex] = useState(0)

  const isLit = streamOn
  const canReplay = state === 'ended' && Boolean(replayEmbedUrl)

  // Only the recording gets its own poster: a pre-event frame showing a thumbnail of the recording
  // would promise a video that does not exist yet. The campaign still is always the last resort.
  const thumbnails = canReplay ? thumbnailSources : []
  const sources = [...thumbnails, posterSrc]
  const index = Math.min(sourceIndex, sources.length - 1)
  const source = sources[index] as string

  const label =
    state === 'ended'
      ? canReplay
        ? null
        : 'Recording currently being uploaded'
      : isLit
        ? `Live - ${eventStamp}`
        : `Premieres ${eventStamp}`

  return (
    <div
      className={cn(
        'relative isolate aspect-video w-full overflow-hidden rounded-[2px]',
        isLit
          ? 'border border-civo-lime shadow-[0_0_16px_#00FF6F1A,0_0_40px_#00FF6F0A] animate-[vr-bloom-breathe_6s_ease-in-out_infinite_alternate] motion-reduce:animate-none'
          : 'border border-civo-cloud/25',
        className,
      )}
    >
      {isLit && liveEmbedUrl ? (
        <StreamEmbed src={liveEmbedUrl} title="The Unveiling live broadcast" />
      ) : canReplay && playing ? (
        <StreamEmbed
          src={withAutoplay(replayEmbedUrl as string)}
          title="The Unveiling - full broadcast"
        />
      ) : (
        <>
          <Image
            src={source}
            alt={index < thumbnails.length ? (thumbnailAlt ?? posterAlt) : posterAlt}
            fill
            priority
            sizes="(min-width: 1440px) 1312px, 100vw"
            unoptimized={source.startsWith('https://img.youtube.com')}
            onError={() => setSourceIndex((index) => Math.min(index + 1, sources.length - 1))}
            className="object-cover"
          />
          {/* Holds the label and the disc off the image without dimming the whole still. */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(2,3,4,0.55) 0%, rgba(2,3,4,0.2) 60%, rgba(2,3,4,0.7) 100%)',
            }}
          />

          {canReplay ? (
            <PlayButton onClick={() => setPlaying(true)} label="Play the recording" />
          ) : null}

          <Corners />
          {label ? (
            <p
              className={cn(
                MONO_MICRO,
                'absolute bottom-5 left-5 z-10',
                isLit ? 'text-civo-lime' : 'text-civo-cloud/60',
              )}
            >
              {label}
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}

/** Four L-brackets inset from the frame edge, per the board. */
function Corners() {
  return (
    <>
      <CornerBracket className="absolute left-4 top-4 text-civo-cloud/40" size={24} />
      <CornerBracket className="absolute right-4 top-4 rotate-90 text-civo-cloud/40" size={24} />
      <CornerBracket className="absolute bottom-4 left-4 -rotate-90 text-civo-cloud/40" size={24} />
      <CornerBracket
        className="absolute bottom-4 right-4 rotate-180 text-civo-cloud/40"
        size={24}
      />
    </>
  )
}

function withAutoplay(embedUrl: string): string {
  return `${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1`
}
